// Lógica de desconto por antecipação, portada do app de cobrança
// (appcobrancav4: src/shared/utils/paymentRules.ts) para o portal.
//
// Regras são por CNPJ, com condições por forma de pagamento (CC/CD/PX/DI) e faixas
// de desconto por quantidade de parcelas antecipadas (min–max). type_discount 'P'
// = percentual, 'V' = valor fixo; limit_value = teto por título (0 = sem teto).
// O desconto só incide sobre títulos cujo mês de vencimento não é anterior ao mês
// atual, e a faixa é escolhida pela quantidade de parcelas do próximo mês em diante.

const REQUIRED_METHODS = ['CC', 'CD', 'PX', 'DI'];

const normalizeMethod = method => {
  if (method === 'R$') return 'DI';
  if (method === 'DI') return 'DI';
  if (method === 'CC' || method === 'CD' || method === 'PX') return method;
  return null;
};

const serializeRanges = ranges =>
  JSON.stringify(
    [...ranges]
      .sort((a, b) => a.min - b.min || a.max - b.max)
      .map(range => ({
        min: range.min,
        max: range.max,
        type_discount: range.type_discount,
        discount: range.discount,
        limit_value: range.limit_value,
      })),
  );

// Retorna faixas únicas se CC/CD/PX/DI tiverem faixas idênticas; senão null
// (aí o desconto depende da forma de pagamento escolhida).
export const getUniformDiscountRanges = conditions => {
  if (!conditions || conditions.length === 0) return null;

  const map = new Map();
  conditions.forEach(condition => {
    const normalized = normalizeMethod(condition.paymentMethod);
    if (!normalized) return;
    map.set(normalized, condition.discountRanges || []);
  });

  if (!REQUIRED_METHODS.every(method => map.has(method))) return null;

  const baseRanges = map.get('CC') || [];
  const baseSerialized = serializeRanges(baseRanges);
  const isUniform = REQUIRED_METHODS.every(
    method => serializeRanges(map.get(method) || []) === baseSerialized,
  );

  return isUniform ? baseRanges : null;
};

export const getDiscountRangesForMethod = (conditions, method) => {
  const normalized = normalizeMethod(method);
  if (!normalized) return null;
  const match = (conditions || []).find(
    condition => normalizeMethod(condition.paymentMethod) === normalized,
  );
  return match ? match.discountRanges || null : null;
};

// Calcula o desconto por título (em centavos, evitando erro de float). A faixa é
// escolhida por `selectedCount` (parcelas antecipadas); títulos com vencimento
// anterior ao mês atual não recebem desconto.
export const calculateDiscountForTitles = (ranges, selectedCount, titles) => {
  const empty = { perTitleCents: titles.map(() => 0), totalDiscount: 0 };
  if (!ranges || selectedCount <= 0 || titles.length === 0) return empty;

  const match = ranges.find(
    range => selectedCount >= range.min && selectedCount <= range.max,
  );
  if (!match) return empty;

  const now = new Date();
  const currentMonthKey = now.getFullYear() * 100 + (now.getMonth() + 1);
  const limitCents =
    match.limit_value && match.limit_value > 0
      ? Math.round(match.limit_value * 100)
      : null;

  const perTitleCents = titles.map(title => {
    const raw = String((title && title.rawDue) || '');
    if (raw.length !== 8) return 0;
    const key = Number(raw);
    if (!Number.isFinite(key)) return 0;
    const monthKey = Math.floor(key / 100);
    if (monthKey < currentMonthKey) return 0;
    const titleCents = Math.round(Number((title && title.amount) || 0) * 100);
    let discountCents =
      match.type_discount === 'P'
        ? Math.round((titleCents * match.discount) / 100)
        : Math.round(match.discount * 100);
    if (limitCents !== null) {
      discountCents = Math.min(discountCents, limitCents);
    }
    return Math.min(discountCents, titleCents);
  });

  const totalDiscount =
    perTitleCents.reduce((acc, value) => acc + value, 0) / 100;
  return { perTitleCents, totalDiscount };
};

// Limite uniforme de parcelas (regras de antecipação). Só retorna um limite se
// CC/CD/PX/DI tiverem o mesmo parcelsLimit; senão null (= sem limite).
export const getUniformParcelsLimit = rules => {
  if (!rules || rules.length === 0) return null;
  const map = new Map();
  rules.forEach(rule => {
    const normalized = normalizeMethod(rule.paymentMethod);
    if (!normalized) return;
    map.set(normalized, rule.parcelsLimit);
  });

  if (!REQUIRED_METHODS.every(method => map.has(method))) return null;

  const baseLimit = map.get('CC');
  if (baseLimit === undefined) return null;

  const isUniform = REQUIRED_METHODS.every(
    method => map.get(method) === baseLimit,
  );
  return isUniform ? baseLimit : null;
};

// ---- Adaptação ao portal ----------------------------------------------------

// vencimentoRaw do portal ('YYYY-MM-DD') -> rawDue do app ('YYYYMMDD').
const toRawDue = vencimentoRaw => String(vencimentoRaw || '').replace(/-/g, '');

// Títulos selecionados do portal -> formato { amount, rawDue }.
const toTitles = selected =>
  (selected || []).map(item => ({
    amount: Number(item.valorRaw || 0),
    rawDue: toRawDue(item.vencimentoRaw),
  }));

// Quantidade de parcelas antecipadas (vencimento do próximo mês em diante).
const countAdvanced = titles => {
  const now = new Date();
  const currentMonthKey = now.getFullYear() * 100 + (now.getMonth() + 1);
  return titles.filter(title => {
    const raw = String(title.rawDue || '');
    if (raw.length !== 8) return false;
    const key = Number(raw);
    if (!Number.isFinite(key)) return false;
    return Math.floor(key / 100) > currentMonthKey;
  }).length;
};

// O endpoint já filtra por CNPJ; usamos a primeira regra do CNPJ.
const getRuleForCnpj = (discountRules, cnpj) => {
  if (!discountRules || !discountRules.length) return null;
  return discountRules.find(rule => rule.cnpj === cnpj) || discountRules[0];
};

// Fachada de cálculo do desconto para os títulos selecionados.
// `method`: 'PX' | 'CC' | null. Com null (etapa de seleção, antes de escolher a
// forma) só aplica desconto se as regras forem uniformes entre as formas.
// Retorna { perTitleCents, totalDiscount, ranges } — perTitleCents alinhado a
// `selected` pela mesma ordem.
export const computeDiscount = (discountRules, cnpj, selected, method) => {
  const titles = toTitles(selected);
  const empty = {
    perTitleCents: titles.map(() => 0),
    totalDiscount: 0,
    ranges: null,
  };

  const rule = getRuleForCnpj(discountRules, cnpj);
  if (!rule) return empty;
  const conditions = rule.conditions || [];

  let ranges = getUniformDiscountRanges(conditions);
  if (!ranges) {
    if (!method) return empty;
    ranges = getDiscountRangesForMethod(conditions, method);
  }
  if (!ranges) return empty;

  const advancedCount = countAdvanced(titles);
  const { perTitleCents, totalDiscount } = calculateDiscountForTitles(
    ranges,
    advancedCount,
    titles,
  );
  return { perTitleCents, totalDiscount, ranges };
};

// Limite de parcelas selecionáveis a partir do array de regras de antecipação.
export const getAnticipationLimit = (anticipationRules, cnpj) => {
  if (!anticipationRules || !anticipationRules.length) return null;
  const doc =
    anticipationRules.find(rule => rule.cnpj === cnpj) || anticipationRules[0];
  return getUniformParcelsLimit((doc && doc.rules) || []);
};
